import {Meter, meterSchema} from './event-alarms/event-alarm.model';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable, signal} from '@angular/core';
import {DateTime} from 'luxon';
import {mapParseItem, mapParseList, TranslateService} from '@brunata-angular/util';
import {environment} from 'src/environments/environment';
import {BehaviorSubject, map, Observable} from 'rxjs';
import {MeterData, meterDataMasterSchema} from './meter-data/meter-data.model';
import {LocationMeter, locationMeterSchema} from './meter-list/location-meter.model';
import {Reading, readingSchema} from './meter-data/readings/reading.model';
import {Sorting} from '@brunata-angular/ui';
import {SelectableItem} from '@brunata-angular/ui';
import {interceptorSkipHeader} from '@shared/utils/errorInterceptor';
import {
    PictureDocument,
    pictureDocumentsSchema,
} from '@shared/ui/gallery/web-archive-document.model';
import {
    MeasuringPoint,
    measuringPointsSchema,
} from './meter-and-sensors/measuring-points-table/measuring-point.model';
import {z} from 'zod';
import {MeasuringPointFilter} from './meter-and-sensors/measuring-points-table/measuring-points-table.component';
import {MeterEvent, meterEventSchema} from './event-alarms-page/event-alarm.model';
import {downloadFile, downloadResponseAsFile} from '@brunata-angular/util';

// A hygro meter has two rows with one meterId, so a row is keyed by meterId and alloUnitType.
export type MeasuringPointKey = {
    meterId: string;
    alloUnitType: string;
};

export type FilterData = {
    proBraLoc: string | null;
    address: string | null;
    meterSuperTypeNo: SelectableItem[] | null;
    alloUnitType: SelectableItem[] | null;
    serialNo: string | null;
    meterModel: string | null;
    scale: string | null;
    unitFactor: string | null;
    mountingDate: DateTime | null;
    roomId: string | null;
    meterBuildLength: SelectableItem[] | null;
    meterConnectionType: SelectableItem[] | null;
    shutoffValve: SelectableItem[] | null;
    supplyOrReturnFlow: SelectableItem[] | null;
    dinRail: SelectableItem[] | null;
    maxCurrent: SelectableItem[] | null;
    sensorPocket: SelectableItem[] | null;
    meterSize: SelectableItem[] | null;
    meterConnectionTypeId: string | null;
    meterSequenceNo: string | null;
    radioTechnology: SelectableItem[] | null;
};
export type SelectedColumns = {
    proBraLoc: boolean | null;
    address: boolean | null;
    alloUnitType: boolean | null;
    serialNo: boolean | null;
    meterModel: boolean | null;
    scale: boolean | null;
    unitFactor: boolean | null;
    meterSuperTypeNo: boolean | null;
    mountingDate: boolean | null;
    roomId: boolean | null;
    meterBuildLength: boolean | null;
    meterConnectionType: boolean | null;
    shutoffValve: boolean | null;
    supplyOrReturnFlow: boolean | null;
    dinRail: boolean | null;
    maxCurrent: boolean | null;
    sensorPocket: boolean | null;
    meterSize: boolean | null;
    meterConnectionTypeId: boolean | null;
    meterSequenceNo: boolean | null;
    radioTechnology: boolean | null;
};

@Injectable({
    providedIn: 'root',
})
export class MeterService {
    interceptorSkipHeaders = new HttpHeaders().set(interceptorSkipHeader, '');
    page = signal<number>(1);
    pageSize = signal<number>(20);

    sorting = new BehaviorSubject<Sorting>({column: 'proBraLoc', direction: 'asc'});
    selectedColumns = new BehaviorSubject<SelectedColumns>({
        proBraLoc: true,
        address: true,
        alloUnitType: true,
        serialNo: true,
        meterModel: true,
        scale: false,
        unitFactor: false,
        meterSuperTypeNo: true,
        mountingDate: false,
        roomId: false,
        meterBuildLength: false,
        meterConnectionType: false,
        shutoffValve: false,
        supplyOrReturnFlow: false,
        dinRail: false,
        maxCurrent: false,
        sensorPocket: false,
        meterSize: false,
        meterConnectionTypeId: false,
        meterSequenceNo: false,
        radioTechnology: false,
    });

    meterListFilterSubject: BehaviorSubject<FilterData | null> =
        new BehaviorSubject<FilterData | null>(null);

    measuringPointFilterSubject: BehaviorSubject<MeasuringPointFilter | null> =
        new BehaviorSubject<MeasuringPointFilter | null>(null);

    baseUrl = `${environment.onlineServiceUrl}/buildings`;

    constructor(
        private http: HttpClient,
        private translateService: TranslateService,
    ) {}

    setFilterColumns(filterFormValues: FilterData): void {
        this.meterListFilterSubject.next(filterFormValues);
    }

    setMeasuringFilterColumns(filterFormValues: MeasuringPointFilter): void {
        this.measuringPointFilterSubject.next(filterFormValues);
    }

    setSelectedColumns(selectedColumns: SelectedColumns): void {
        this.selectedColumns.next(selectedColumns);
    }

    updateSorting(sorting: Sorting): void {
        this.sorting.next(sorting);
    }

    getPhysicalMeters(
        buildingNo: string,
        startDate?: DateTime | null,
        endDate?: DateTime | null,
    ): Observable<LocationMeter[]> {
        const params = {
            startdate: startDate?.toISO() ?? '',
            enddate: endDate?.toISO() ?? '',
        };

        const context =
            startDate != null && endDate != null
                ? {withCredentials: true, params: params}
                : {withCredentials: true};

        return this.http
            .get(`${this.baseUrl}/${buildingNo}/physicalmeter`, context)
            .pipe(
                mapParseItem(
                    z
                        .object({physicalMeters: z.array(locationMeterSchema)})
                        .transform(wrapper => wrapper.physicalMeters),
                ),
            );
    }
    getMeterData(buildingNo: string, meterId: string): Observable<MeterData> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/meters/${meterId}`, {
                withCredentials: true,
            })
            .pipe(mapParseItem(meterDataMasterSchema));
    }

    getMeasuringPoints(buildingNo: string, startDate: DateTime): Observable<MeasuringPoint[]> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/measuringpoints`, {
                params: {
                    date: startDate.toISO() ?? '',
                },
                withCredentials: true,
            })
            .pipe(mapParseItem(measuringPointsSchema));
    }

    getEventAlarms(
        buildingNo: string,
        periodStartDate: DateTime,
        periodEndDate: DateTime,
        meterId: string,
    ): Observable<Meter> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/meters/${meterId}/events/latest`, {
                params: {
                    periodstartdate: periodStartDate.toISO() ?? '',
                    periodenddate: periodEndDate.toISO() ?? '',
                },
                withCredentials: true,
                headers: this.interceptorSkipHeaders,
            })
            .pipe(mapParseItem(meterSchema));
    }

    getLatestEvents(
        buildingNo: string,
        periodStartDate: DateTime,
        periodEndDate: DateTime,
    ): Observable<MeterEvent[]> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/events/latest`, {
                params: {
                    periodstartdate: periodStartDate.toISO() ?? '',
                    periodenddate: periodEndDate.toISO() ?? '',
                },
                withCredentials: true,
            })
            .pipe(mapParseList(meterEventSchema));
    }

    getReadings(buildingNo: string, meterId: string, date: DateTime): Observable<Reading[]> {
        return this.http
            .get(`${this.baseUrl}/${buildingNo}/meters/${meterId}/readings`, {
                params: {
                    readingdate: date.toISO() ?? '',
                },
                withCredentials: true,
            })
            .pipe(mapParseList(readingSchema));
    }

    downloadMeasuringPointsExportFile(buildingNo: string, onDate: DateTime): void {
        this.http
            .get(`${this.baseUrl}/${buildingNo}/exports/measuringpoints`, {
                observe: 'response',
                responseType: 'blob',
                params: {
                    date: onDate.toISO() ?? '',
                    languagetag: this.translateService.language,
                },
            })
            .subscribe(response => {
                if (response.body != null) {
                    const fileBlob = new Blob([response.body], {
                        type: 'xlsx',
                    });
                    const fileName =
                        this.translateService.translate('utilities_measuring-points') +
                        '-' +
                        onDate.toISO() +
                        `.xlsx`;
                    downloadFile(fileName, fileBlob);
                }
            });
    }

    downloadSelectedMeasuringPointsExportFile(
        buildingNo: string,
        onDate: DateTime,
        measuringPoints: MeasuringPointKey[],
    ): void {
        this.http
            .post(
                `${this.baseUrl}/${buildingNo}/exports/measuringpoints`,
                {
                    date: onDate.toISO() ?? '',
                    languagetag: this.translateService.language,
                    measuringPoints,
                },
                {observe: 'response', responseType: 'blob'},
            )
            .pipe(
                downloadResponseAsFile(
                    () =>
                        `${this.translateService.translate('utilities_measuring-points')}-${onDate.toISO()}.xlsx`,
                ),
            )
            .subscribe();
    }

    downloadPhysicalMeterExportFile(buildingNo: string): void {
        this.http
            .get(`${this.baseUrl}/${buildingNo}/exports/physicalmetersandsensors`, {
                observe: 'response',
                responseType: 'blob',
                params: {
                    languagetag: this.translateService.language,
                },
            })
            .subscribe(response => {
                if (response.body != null) {
                    const fileBlob = new Blob([response.body], {
                        type: 'xlsx',
                    });

                    const fileName =
                        this.translateService.translate('utilities_meter_and_sensor_overview') +
                        '-' +
                        DateTime.now().toString() +
                        `.xlsx`;

                    downloadFile(fileName, fileBlob);
                }
            });
    }
    downloadLatestEventExportFile(
        buildingNo: string,
        startDate: DateTime,
        endDate: DateTime,
    ): void {
        this.http
            .get(`${this.baseUrl}/${buildingNo}/exports/latestevents`, {
                observe: 'response',
                responseType: 'blob',
                params: {
                    languagetag: this.translateService.language,
                    startdate: startDate.toISO() ?? '',
                    enddate: endDate.toISO() ?? '',
                },
            })
            .subscribe(response => {
                if (response.body != null) {
                    const fileBlob = new Blob([response.body], {
                        type: 'xlsx',
                    });

                    const fileName =
                        this.translateService.translate('utilities_latest_event') +
                        '-' +
                        DateTime.now().toString() +
                        `.xlsx`;
                    downloadFile(fileName, fileBlob);
                }
            });
    }

    searchPicturesByMeterNo(meterId: string): Observable<PictureDocument[]> {
        return this.http
            .get<PictureDocument[]>(
                `${environment.webArchiveServiceUrl}/documents/search/picturedoc`,
                {
                    withCredentials: true,
                    params: {meterid: meterId},
                },
            )
            .pipe(
                mapParseItem(pictureDocumentsSchema),
                map(result => result.documents),
            );
    }
}
